import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  rfwTrivias,
  getAllTrivias,
  getTriviasByType,
  getTriviasByMode,
  getTriviasByStatus,
  getActiveTrivias,
  getUpcomingTrivias,
  getCompletedTrivias,
  getTriviaById,
  getRecentWinners,
  triviaTypeLabels,
  triviaTypeIcons,
  triviaTypeColors,
  triviaTypeBgLight,
  triviaTypeTextColors,
  triviaModeLabels,
  triviaModeIcons,
  triviaModeColors,
  triviaDashboardStats,
  triviaGlobalRanking,
  triviaBadges,
  formatCount,
  formatTriviaDate,
  getTimeRemainingText,
  type Trivia,
  type TriviaType,
  type TriviaMode,
  type TriviaStatus,
  type TriviaQuestion,
  type TriviaWinner,
} from '@/mocks/rfwTrivias';

type PageState = 'loading' | 'error' | 'ready';
type GameState = 'idle' | 'playing' | 'finished';
type DetailTab = 'info' | 'resultados' | 'ganadores' | 'score';

const GAME_HISTORY_KEY = 'rfw_trivia_history';

function loadGameHistory(): Record<string, { score: number; correctAnswers: number; totalQuestions: number; time: string; playedAt: string }> {
  try {
    const raw = localStorage.getItem(GAME_HISTORY_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveGameHistory(data: Record<string, unknown>) {
  localStorage.setItem(GAME_HISTORY_KEY, JSON.stringify(data));
}

export default function TriviasPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [typeFilter, setTypeFilter] = useState<TriviaType | 'todos'>('todos');
  const [modeFilter, setModeFilter] = useState<TriviaMode | 'todos'>('todos');
  const [statusFilter, setStatusFilter] = useState<TriviaStatus | 'todos'>('todos');
  const [selectedTrivia, setSelectedTrivia] = useState<Trivia | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('info');

  // Game state
  const [gameState, setGameState] = useState<GameState>('idle');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [questionResults, setQuestionResults] = useState<boolean[]>([]);
  const [gameStartTime, setGameStartTime] = useState<number>(0);
  const [gameEndTime, setGameEndTime] = useState<number>(0);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [gameHistory, setGameHistory] = useState<Record<string, unknown>>(() => loadGameHistory());

  const [filteredTab, setFilteredTab] = useState<'all' | 'ranking' | 'badges'>('all');

  useEffect(() => {
    const t = setTimeout(() => setPageState('ready'), 500);
    return () => clearTimeout(t);
  }, []);

  // Timer
  useEffect(() => {
    if (gameState === 'playing' && selectedTrivia) {
      const questionTime = selectedTrivia.timePerQuestion || 15;
      setTimeLeft(questionTime);
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [gameState, currentQuestionIndex]);

  const handleTimeUp = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    const isCorrect = false;
    setQuestionResults((prev) => [...prev, isCorrect]);

    if (selectedTrivia && currentQuestionIndex < selectedTrivia.questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex((prev) => prev + 1);
        setSelectedAnswer(null);
      }, 1500);
    } else {
      finishGame([...questionResults, false], correctCount);
    }
  }, [currentQuestionIndex, correctCount, questionResults, selectedTrivia]);

  const startGame = useCallback((trivia: Trivia) => {
    setSelectedTrivia(trivia);
    setGameState('playing');
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setCorrectCount(0);
    setQuestionResults([]);
    setGameStartTime(Date.now());
    setDetailTab('info');
  }, []);

  const handleAnswer = useCallback((optionIndex: number) => {
    if (selectedAnswer !== null || gameState !== 'playing' || !selectedTrivia) return;
    if (timerRef.current) clearInterval(timerRef.current);

    const currentQ = selectedTrivia.questions[currentQuestionIndex];
    const isCorrect = optionIndex === currentQ.correctAnswer;
    setSelectedAnswer(optionIndex);
    const newResults = [...questionResults, isCorrect];
    setQuestionResults(newResults);
    if (isCorrect) setCorrectCount((prev) => prev + 1);

    if (currentQuestionIndex < selectedTrivia.questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex((prev) => prev + 1);
        setSelectedAnswer(null);
      }, 1200);
    } else {
      const finalCorrect = isCorrect ? correctCount + 1 : correctCount;
      finishGame(newResults, finalCorrect);
    }
  }, [selectedAnswer, gameState, selectedTrivia, currentQuestionIndex, questionResults, correctCount]);

  const finishGame = useCallback((results: boolean[], totalCorrect: number) => {
    const endTime = Date.now();
    setGameEndTime(endTime);
    setGameState('finished');

    if (selectedTrivia) {
      const elapsedMs = endTime - gameStartTime;
      const minutes = Math.floor(elapsedMs / 60000);
      const seconds = Math.floor((elapsedMs % 60000) / 1000);
      const timeStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

      const history = loadGameHistory();
      history[selectedTrivia.id] = {
        score: totalCorrect * 100,
        correctAnswers: totalCorrect,
        totalQuestions: selectedTrivia.totalQuestions,
        time: timeStr,
        playedAt: new Date().toISOString(),
      };
      saveGameHistory(history);
      setGameHistory(history);
    }
  }, [selectedTrivia, gameStartTime]);

  const resetGame = useCallback(() => {
    setGameState('idle');
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setCorrectCount(0);
    setQuestionResults([]);
  }, []);

  const getGameTime = useCallback((): string => {
    const elapsedMs = gameEndTime - gameStartTime;
    const minutes = Math.floor(elapsedMs / 60000);
    const seconds = Math.floor((elapsedMs % 60000) / 1000);
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  }, [gameEndTime, gameStartTime]);

  const filtered = useMemo(() => {
    let result = getAllTrivias();
    if (typeFilter !== 'todos') result = result.filter((t) => t.type === typeFilter);
    if (modeFilter !== 'todos') result = result.filter((t) => t.mode === modeFilter);
    if (statusFilter !== 'todos') result = result.filter((t) => t.status === statusFilter);
    result.sort((a, b) => {
      const order: Record<string, number> = { live: 0, active: 1, upcoming: 2, completed: 3 };
      if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    });
    return result;
  }, [typeFilter, modeFilter, statusFilter]);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => setPageState('ready'), 500);
  }, []);

  const typeTabs: { id: TriviaType | 'todos'; label: string; icon: string }[] = [
    { id: 'todos', label: 'Todos', icon: 'ri-grid-line' },
    { id: 'music', label: 'Música', icon: triviaTypeIcons.music },
    { id: 'radio', label: 'Radio', icon: triviaTypeIcons.radio },
    { id: 'program', label: 'Programa', icon: triviaTypeIcons.program },
    { id: 'host', label: 'Locutor', icon: triviaTypeIcons.host },
    { id: 'sponsored', label: 'Patrocinada', icon: triviaTypeIcons.sponsored },
  ];

  const modeTabs: { id: TriviaMode | 'todos'; label: string; icon: string }[] = [
    { id: 'todos', label: 'Todos', icon: 'ri-grid-line' },
    { id: 'quick', label: 'Rápida', icon: triviaModeIcons.quick },
    { id: 'scheduled', label: 'Programada', icon: triviaModeIcons.scheduled },
    { id: 'tournament', label: 'Torneo', icon: triviaModeIcons.tournament },
  ];

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <section className="relative w-full h-[280px] md:h-[380px] bg-background-200 animate-pulse" />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 space-y-8">
          <div className="flex gap-2 flex-wrap">{[1,2,3,4,5,6].map((i) => (<div key={i} className="bg-background-100 rounded-full h-8 w-20 animate-pulse" />))}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3,4,5,6].map((i) => (<div key={i} className="bg-background-100 rounded-2xl h-[320px] animate-pulse" />))}</div>
        </main>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Error al cargar trivias</h2>
        <p className="text-sm text-foreground-500 mb-6 text-center max-w-md">No pudimos cargar las trivias. Verificá tu conexión e intentá de nuevo.</p>
        <button onClick={handleRetry} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line"></i>Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Interactive%20game%20show%20quiz%20studio%20with%20colorful%20neon%20lights%20podiums%20and%20buzzers%2C%20large%20LED%20screens%20displaying%20trivia%20questions%2C%20energetic%20atmosphere%20with%20dramatic%20lighting%20and%20excitement%2C%20modern%20broadcast%20television%20studio%20production%20photography&width=1600&height=900&seq=rfw-trivias-hero&orientation=landscape"
          alt="Trivias RFW"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
            <i className="ri-questionnaire-line text-3xl text-background-50"></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Sistema de Trivias RFW
          </h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
            Demostrá lo que sabés. Música, Radio, Programas, Locutores y Patrocinadas. Competí, respondé, sumá Score y ganá premios reales.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <span className="text-background-50/60 text-xs">{rfwTrivias.length} trivias · {triviaDashboardStats.activeNow} activas · {formatCount(triviaDashboardStats.totalParticipants)} participantes</span>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8">
        {/* STATS STRIP */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Trivias Totales', value: triviaDashboardStats.totalTrivias.toString(), icon: 'ri-questionnaire-line', color: 'text-primary-600 bg-primary-50' },
            { label: 'Activas Ahora', value: triviaDashboardStats.activeNow.toString(), icon: 'ri-flashlight-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Participantes', value: formatCount(triviaDashboardStats.totalParticipants), icon: 'ri-group-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Respuestas', value: formatCount(triviaDashboardStats.totalQuestionsAnswered), icon: 'ri-chat-check-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Premios', value: triviaDashboardStats.prizesAwarded.toString(), icon: 'ri-gift-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* TYPE FILTERS */}
        <div className="flex flex-wrap items-center gap-1.5">
          {typeTabs.map((tab) => {
            const isActive = typeFilter === tab.id;
            const typeColor = tab.id !== 'todos' ? triviaTypeTextColors[tab.id as TriviaType] : 'text-foreground-600';
            const typeBg = tab.id !== 'todos' ? triviaTypeBgLight[tab.id as TriviaType] : 'bg-background-100';
            return (
              <button
                key={tab.id}
                onClick={() => setTypeFilter(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                  isActive ? `border-current shadow-sm ${typeColor} ${typeBg}` : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* MODE + STATUS FILTERS */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {modeTabs.map((tab) => {
              const isActive = modeFilter === tab.id;
              const modeColor = tab.id !== 'todos' ? triviaModeColors[tab.id as TriviaMode] : '';
              return (
                <button
                  key={tab.id}
                  onClick={() => setModeFilter(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    isActive ? modeColor : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${tab.icon} text-[10px]`}></i>
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-1.5">
            {(['todos', 'live', 'active', 'upcoming', 'completed'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  statusFilter === s ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {s === 'todos' ? 'Todas' : s === 'live' ? 'En Vivo' : s === 'active' ? 'Activas' : s === 'upcoming' ? 'Próximas' : 'Completadas'}
              </button>
            ))}
          </div>
        </div>

        {/* RESULTS COUNT */}
        <p className="text-[11px] text-foreground-400">
          {filtered.length === 0 ? 'No se encontraron trivias con esos filtros.' : `Mostrando ${filtered.length} trivia${filtered.length !== 1 ? 's' : ''}`}
        </p>

        {/* TRIVIAS GRID */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((trivia) => {
              const isLive = trivia.status === 'live';
              const isCompleted = trivia.status === 'completed';
              const isUpcoming = trivia.status === 'upcoming';
              const timeText = getTimeRemainingText(trivia.endDate);
              const canPlay = isLive || trivia.status === 'active';

              return (
                <div
                  key={trivia.id}
                  className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-300/60 transition-all duration-300 group cursor-pointer"
                  onClick={() => { setSelectedTrivia(trivia); resetGame(); setDetailTab(canPlay ? 'info' : 'resultados'); }}
                >
                  <div className={`h-2 bg-gradient-to-r ${
                    trivia.type === 'music' ? 'from-pink-400 to-rose-400' :
                    trivia.type === 'radio' ? 'from-cyan-400 to-blue-400' :
                    trivia.type === 'program' ? 'from-teal-400 to-emerald-400' :
                    trivia.type === 'host' ? 'from-amber-400 to-orange-400' :
                    'from-violet-400 to-purple-400'
                  }`}></div>
                  <div className="p-4">
                    <div className="flex items-center gap-1.5 mb-2">
                      {isLive && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>EN VIVO
                        </span>
                      )}
                      {trivia.status === 'active' && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[9px] font-bold`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>Activa
                        </span>
                      )}
                      {isCompleted && <span className="px-2 py-0.5 rounded-full bg-foreground-100 text-foreground-500 text-[9px] font-medium">Completada</span>}
                      {isUpcoming && <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 text-[9px] font-medium">Próxima</span>}
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-medium border ${triviaTypeColors[trivia.type]}`}>
                        {triviaTypeLabels[trivia.type]}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[9px] font-medium">
                        {triviaModeLabels[trivia.mode]}
                      </span>
                    </div>

                    <p className="text-sm font-bold text-foreground-900 leading-snug mb-1.5 line-clamp-2">{trivia.title}</p>
                    <p className="text-[11px] text-foreground-500 leading-snug mb-3 line-clamp-2">{trivia.description}</p>

                    <div className="flex items-center gap-2 text-[10px] mb-3">
                      <span className="flex items-center gap-1 text-foreground-500">
                        <i className="ri-radio-line text-[9px]"></i>
                        {trivia.entityName}
                      </span>
                      {trivia.sponsorName && (
                        <span className="flex items-center gap-1 text-violet-600 font-semibold">
                          <i className="ri-vip-diamond-line text-[9px]"></i>
                          {trivia.sponsorName}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center gap-3 text-foreground-500">
                        <span className="flex items-center gap-1"><i className="ri-questionnaire-line text-[9px]"></i>{trivia.totalQuestions} preg.</span>
                        <span className="flex items-center gap-1"><i className="ri-group-line text-[9px]"></i>{formatCount(trivia.participants)}</span>
                      </div>
                      <span className="text-primary-600 font-bold flex items-center gap-0.5">
                        <i className="ri-flashlight-line text-[9px]"></i>+{trivia.score.win} pts
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-questionnaire-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-400">No se encontraron trivias</p>
            <p className="text-[12px] text-foreground-300 mt-1">Intentá ajustar los filtros</p>
          </div>
        )}

        {/* TABS: Ranking + Badges */}
        <div className="flex items-center gap-1.5 mb-4">
          {[
            { id: 'all' as const, label: 'Trivias', icon: 'ri-grid-line' },
            { id: 'ranking' as const, label: 'Ranking Trivia', icon: 'ri-medal-line' },
            { id: 'badges' as const, label: 'Insignias', icon: 'ri-shield-star-line' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilteredTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                filteredTab === tab.id ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className={`${tab.icon} text-[10px]`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* RANKING */}
        {filteredTab === 'ranking' && (
          <div className="bg-background-100 rounded-3xl p-6 md:p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-medal-line text-xl text-amber-600"></i>
              </div>
              <h3 className="text-lg font-extrabold text-foreground-950 mb-2">Ranking Global de Trivias</h3>
              <p className="text-xs text-foreground-500">Los 10 mejores jugadores de trivias en toda la plataforma RFW.</p>
            </div>
            <div className="space-y-2">
              {triviaGlobalRanking.map((entry, i) => (
                <div key={i} className={`flex items-center gap-4 p-3 rounded-xl ${
                  i < 3 ? 'bg-amber-50 border border-amber-200' : 'bg-background-50 border border-background-200/70'
                }`}>
                  <span className="w-8 text-center text-lg font-extrabold text-foreground-600 shrink-0">
                    {entry.position === 1 ? '🥇' : entry.position === 2 ? '🥈' : entry.position === 3 ? '🥉' : `#${entry.position}`}
                  </span>
                  <img src={entry.avatarUrl} alt={entry.name} className="w-10 h-10 rounded-full object-cover shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-foreground-900">{entry.name}</p>
                    <div className="flex items-center gap-3 text-[10px] text-foreground-500">
                      <span>{entry.totalScore} pts</span>
                      <span>·</span>
                      <span>{entry.triviasWon} ganadas</span>
                      <span>·</span>
                      <span>{entry.totalParticipated} jugadas</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-center">
                      <p className="text-sm font-bold text-emerald-600">{entry.accuracy}%</p>
                      <p className="text-[9px] text-foreground-400">Precisión</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-bold text-foreground-700">{entry.avgTime}</p>
                      <p className="text-[9px] text-foreground-400">Tiempo</p>
                    </div>
                    {entry.badge && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold">{entry.badge}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* BADGES */}
        {filteredTab === 'badges' && (
          <div className="bg-background-100 rounded-3xl p-6 md:p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-shield-star-line text-xl text-violet-600"></i>
              </div>
              <h3 className="text-lg font-extrabold text-foreground-950 mb-2">Insignias de Trivias</h3>
              <p className="text-xs text-foreground-500">Desbloqueá insignias exclusivas demostrando tu conocimiento en las trivias RFW.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {triviaBadges.map((badge) => (
                <div key={badge.id} className={`p-5 rounded-2xl border ${badge.color} ${badge.bgColor}`}>
                  <div className="text-center">
                    <span className="text-4xl block mb-3">{badge.emoji}</span>
                    <p className="text-sm font-extrabold text-foreground-900 mb-1.5">{badge.name}</p>
                    <p className="text-[11px] text-foreground-500 leading-snug mb-3">{badge.description}</p>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50 border border-background-200/70">
                      <i className="ri-check-line text-[10px] text-emerald-600"></i>
                      <span className="text-[10px] font-semibold text-foreground-700">{badge.requirement}</span>
                    </div>
                    <p className="text-xs font-bold text-primary-600 mt-3">+{badge.scoreBonus} pts RFW</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CROSS LINKS */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/rankings" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-accent-200/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-trophy-line text-base text-accent-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Rankings RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Conocé quiénes lideran en cada categoría</p>
          </Link>
          <Link to="/copas" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-amber-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-trophy-line text-base text-amber-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Copas RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Competí en los campeonatos permanentes</p>
          </Link>
          <Link to="/competencias" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-red-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-sword-line text-base text-red-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Competencias RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Batallas 1v1 entre radios, programas y más</p>
          </Link>
        </section>

        {/* REGLA FINAL */}
        <section className="p-6 rounded-2xl bg-primary-100/70 border border-primary-200/50 text-center">
          <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-questionnaire-line text-xl text-white"></i>
          </div>
          <p className="text-lg font-extrabold text-foreground-950 mb-2">Las trivias aumentan la permanencia y participación.</p>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            No solo se trata de saber — se trata de competir, aprender y conectar con tu comunidad RFW. Cada respuesta correcta es un oyente más comprometido. Cada trivia completada es tiempo de calidad en la plataforma.
          </p>
        </section>
      </main>

      {/* ─── TRIVIA DETAIL / GAME MODAL ─── */}
      {selectedTrivia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setSelectedTrivia(null); resetGame(); }}></div>
          <div className="relative bg-background-50 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <button
              onClick={() => { setSelectedTrivia(null); resetGame(); }}
              className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-background-50/80 backdrop-blur-md flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-foreground-700"></i>
            </button>

            {gameState === 'idle' ? (
              <div className="p-5 md:p-6">
                {/* Header */}
                <div className="flex items-center gap-2 mb-3">
                  {selectedTrivia.mode === 'tournament' && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">TORNEO</span>
                  )}
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${triviaTypeColors[selectedTrivia.type]}`}>
                    {triviaTypeLabels[selectedTrivia.type]}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium">
                    {triviaModeLabels[selectedTrivia.mode]}
                  </span>
                  {selectedTrivia.sponsorName && (
                    <span className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-[10px] font-medium">Patrocinada</span>
                  )}
                </div>

                <h2 className="text-lg md:text-xl font-extrabold text-foreground-950 leading-tight mb-2">{selectedTrivia.title}</h2>
                <p className="text-xs text-foreground-600 leading-relaxed mb-4">{selectedTrivia.description}</p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  <div className="text-center p-3 rounded-xl bg-background-100">
                    <i className="ri-questionnaire-line text-lg text-primary-600 block mb-1"></i>
                    <p className="text-sm font-bold text-foreground-900">{selectedTrivia.totalQuestions}</p>
                    <p className="text-[10px] text-foreground-500">Preguntas</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-background-100">
                    <i className="ri-timer-line text-lg text-amber-600 block mb-1"></i>
                    <p className="text-sm font-bold text-foreground-900">{selectedTrivia.timePerQuestion}s</p>
                    <p className="text-[10px] text-foreground-500">Por Pregunta</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-background-100">
                    <i className="ri-group-line text-lg text-emerald-600 block mb-1"></i>
                    <p className="text-sm font-bold text-foreground-900">{formatCount(selectedTrivia.participants)}</p>
                    <p className="text-[10px] text-foreground-500">Participantes</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-background-100">
                    <i className="ri-flashlight-line text-lg text-violet-600 block mb-1"></i>
                    <p className="text-sm font-bold text-foreground-900">+{selectedTrivia.score.win}</p>
                    <p className="text-[10px] text-foreground-500">Pts al Ganar</p>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 mb-5">
                  <p className="text-xs font-bold text-foreground-900 mb-1">🏆 Premio: {selectedTrivia.prize}</p>
                  <p className="text-[11px] text-foreground-600 leading-snug">{selectedTrivia.prizeDescription}</p>
                </div>

                <div className="flex gap-3">
                  {(selectedTrivia.status === 'live' || selectedTrivia.status === 'active') && (
                    <button
                      onClick={() => startGame(selectedTrivia)}
                      className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-bold rounded-full transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-circle-line"></i>
                      ¡Jugar Ahora!
                    </button>
                  )}
                  {selectedTrivia.status === 'completed' && (
                    <div className="flex-1 text-center py-3">
                      <p className="text-sm font-semibold text-foreground-500">Esta trivia ya finalizó</p>
                      <p className="text-[10px] text-foreground-400">Mirá los resultados abajo</p>
                    </div>
                  )}
                </div>

                {/* Completed: show winners */}
                {selectedTrivia.topWinners.length > 0 && (
                  <div className="mt-5 pt-4 border-t border-background-200/70">
                    <p className="text-xs font-bold text-foreground-900 mb-3">🏆 Ganadores</p>
                    <div className="space-y-2">
                      {selectedTrivia.topWinners.map((w, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100">
                          <span className="text-lg">{w.position === 1 ? '🥇' : w.position === 2 ? '🥈' : '🥉'}</span>
                          <img src={w.avatarUrl} alt={w.name} className="w-8 h-8 rounded-full object-cover" />
                          <div>
                            <p className="text-xs font-semibold text-foreground-900">{w.name}</p>
                            <p className="text-[10px] text-foreground-500">{w.correctAnswers}/{w.totalQuestions} · {w.score} pts · {w.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : gameState === 'playing' && selectedTrivia ? (
              <div className="p-5 md:p-6">
                {/* Game progress bar */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-foreground-600">Pregunta {currentQuestionIndex + 1}/{selectedTrivia.totalQuestions}</span>
                    <span className={`text-[10px] font-semibold ${correctCount > 0 ? 'text-emerald-600' : 'text-foreground-400'}`}>
                      {correctCount} {correctCount === 1 ? 'acierto' : 'aciertos'}
                    </span>
                  </div>
                  <div className={`flex items-center gap-1.5 text-sm font-bold ${timeLeft <= 5 ? 'text-red-600 animate-pulse' : timeLeft <= 10 ? 'text-amber-600' : 'text-foreground-700'}`}>
                    <i className="ri-timer-line text-xs"></i>
                    {timeLeft}s
                  </div>
                </div>

                {/* Progress bar */}
                <div className="h-1.5 rounded-full bg-background-200 mb-5 overflow-hidden">
                  <div className="h-full rounded-full bg-primary-500 transition-all duration-300" style={{ width: `${((currentQuestionIndex) / selectedTrivia.totalQuestions) * 100}%` }}></div>
                </div>

                {/* Question */}
                <div className="p-4 rounded-2xl bg-primary-50 border border-primary-100 mb-5">
                  <p className="text-sm font-bold text-foreground-900 leading-snug">{selectedTrivia.questions[currentQuestionIndex].question}</p>
                </div>

                {/* Options */}
                <div className="space-y-2">
                  {selectedTrivia.questions[currentQuestionIndex].options.map((opt, idx) => {
                    const currentQ = selectedTrivia.questions[currentQuestionIndex];
                    const isCorrectOption = idx === currentQ.correctAnswer;
                    const isSelected = selectedAnswer === idx;
                    const showResult = selectedAnswer !== null;

                    let bgClass = 'bg-background-50 border-background-200/70 hover:border-primary-300/60';
                    if (showResult && isCorrectOption) bgClass = 'bg-emerald-50 border-emerald-400';
                    else if (showResult && isSelected && !isCorrectOption) bgClass = 'bg-red-50 border-red-400';
                    else if (showResult && !isSelected && !isCorrectOption) bgClass = 'bg-background-50/50 border-background-200/50 opacity-60';

                    return (
                      <button
                        key={idx}
                        onClick={() => handleAnswer(idx)}
                        disabled={selectedAnswer !== null}
                        className={`w-full text-left p-3 rounded-xl border transition-all duration-200 ${bgClass} ${selectedAnswer === null ? 'cursor-pointer' : 'cursor-default'}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-background-200 flex items-center justify-center text-[10px] font-bold text-foreground-600 shrink-0">
                            {String.fromCharCode(65 + idx)}
                          </span>
                          <span className="text-sm font-medium text-foreground-800 flex-1">{opt}</span>
                          {showResult && isCorrectOption && (
                            <i className="ri-check-line text-emerald-600 text-base shrink-0"></i>
                          )}
                          {showResult && isSelected && !isCorrectOption && (
                            <i className="ri-close-line text-red-600 text-base shrink-0"></i>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Time bar */}
                <div className="mt-5 h-1 rounded-full bg-background-200 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ${timeLeft <= 5 ? 'bg-red-500' : timeLeft <= 10 ? 'bg-amber-500' : 'bg-primary-500'}`}
                    style={{ width: `${(timeLeft / (selectedTrivia.timePerQuestion || 15)) * 100}%` }}
                  ></div>
                </div>
              </div>
            ) : gameState === 'finished' && selectedTrivia ? (
              <div className="p-5 md:p-6 text-center">
                <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">{correctCount >= selectedTrivia.totalQuestions ? '🏆' : correctCount >= selectedTrivia.totalQuestions * 0.7 ? '⭐' : correctCount >= selectedTrivia.totalQuestions * 0.5 ? '👍' : '💪'}</span>
                </div>
                <h2 className="text-xl font-extrabold text-foreground-950 mb-2">
                  {correctCount >= selectedTrivia.totalQuestions ? '¡PERFECTO!' : correctCount >= selectedTrivia.totalQuestions * 0.7 ? '¡Muy Bien!' : correctCount >= selectedTrivia.totalQuestions * 0.5 ? '¡Buen Intento!' : '¡Seguí Practicando!'}
                </h2>
                <p className="text-sm text-foreground-600 mb-5">
                  Respondiste {correctCount} de {selectedTrivia.totalQuestions} correctamente en {getGameTime()}.
                </p>

                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="p-3 rounded-xl bg-emerald-50">
                    <p className="text-xl font-extrabold text-emerald-600">{correctCount}</p>
                    <p className="text-[10px] text-emerald-700">Aciertos</p>
                  </div>
                  <div className="p-3 rounded-xl bg-amber-50">
                    <p className="text-xl font-extrabold text-amber-600">{Math.round((correctCount / selectedTrivia.totalQuestions) * 100)}%</p>
                    <p className="text-[10px] text-amber-700">Precisión</p>
                  </div>
                  <div className="p-3 rounded-xl bg-violet-50">
                    <p className="text-xl font-extrabold text-violet-600">+{correctCount * 100}</p>
                    <p className="text-[10px] text-violet-700">Puntos RFW</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => startGame(selectedTrivia)}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-bold rounded-full transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-refresh-line"></i>
                    Jugar de Nuevo
                  </button>
                  <button
                    onClick={() => { setSelectedTrivia(null); resetGame(); }}
                    className="px-6 py-3 bg-background-100 text-foreground-600 font-semibold rounded-full hover:bg-background-200 transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}